import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  BarChart3,
  Phone,
  ClipboardList,
  CreditCard,
  Settings,
  Bell,
  MessageSquare,
  Zap,
  Inbox,
  Link2,
  MoreHorizontal,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  ShieldCheck,
  Search,
  BadgePercent,
  Wrench,
  MapPinned,
  Ticket,
  AlertTriangle,
  Building2,
  Smartphone,
} from 'lucide-react';
import useAppEnquiryWhatsAppNotify from '../../hooks/useAppEnquiryWhatsAppNotify';

const MENU_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: Users, label: 'Users', path: '/users' },
  { icon: Wrench, label: 'Mechanic Management', path: '/mechanic-management' },
  { icon: ShieldCheck, label: 'Mechanic KYC', path: '/mechanic-kyc' },
  { icon: MapPinned, label: 'Auto Mechanic Assignment City', path: '/mechanic-assignment-cities' },
  { icon: Ticket, label: 'Partner RSA Tickets', path: '/partner-rsa-tickets' },
  { icon: Building2, label: 'Enterprises (B2B)', path: '/enterprises' },
  { icon: Smartphone, label: 'App Version Control', path: '/app-version-control' },
  { icon: CreditCard, label: 'Payout Requests', path: '/payout-requests' },
  { icon: FileText, label: 'Sales Reports', path: '/sales-report' },
  { icon: BarChart3, label: 'Service Reports', path: '/service-report' },
  { icon: Phone, label: 'Call Report', path: '/call-report' },
  { icon: AlertTriangle, label: 'Fault Detector', path: '/fault-detector' },
  { icon: MessageSquare, label: 'WhatsApp', path: '/whatsapp' },
  { icon: Zap, label: 'WA Automation', path: '/whatsapp-automation' },
  { icon: BadgePercent, label: 'Coupons', path: '/coupons' },
  { icon: Inbox, label: 'App Enquiries', path: '/app-enquiries' },
  { icon: UserPlus, label: 'Add Customer', path: '/add-customer' },
  { icon: ShieldCheck, label: 'Physical Card Verify', path: '/physical-card-verifications' },
  { icon: Link2, label: 'URL Manage', path: '/url-manage' },
  { icon: Bell, label: 'Notifications', path: '/notifications' },
  { icon: Settings, label: 'Settings', path: '/settings' },
  { icon: ClipboardList, label: 'WFH Employee Report', path: '/wfh-employee-report' },
  { icon: MoreHorizontal, label: 'More Options', path: '/more' },
];

const AdminLayout = ({ children, user, onLogout }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [hoverPeek, setHoverPeek] = useState(false);
  const [menuSearch, setMenuSearch] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // WhatsApp alert as soon as enquiry inserts — even if App Enquiries tab is closed
  useAppEnquiryWhatsAppNotify(true);

  // Expanded by preference stays expanded; collapsed peeks open only on hover.
  const sidebarExpanded = !sidebarCollapsed || hoverPeek;

  const filteredMenuItems = useMemo(() => {
    const q = menuSearch.trim().toLowerCase();
    if (!q) return MENU_ITEMS;
    return MENU_ITEMS.filter((item) => item.label.toLowerCase().includes(q));
  }, [menuSearch]);

  const isActive = (path) => {
    if (!path) return false;
    if (location.pathname === path) return true;
    return location.pathname.startsWith(`${path}/`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <h1 className="ml-3 text-xl font-bold text-gray-900">RoadServe Admin</h1>
        </div>
        <button
          onClick={onLogout}
          className="p-2 rounded-lg hover:bg-gray-100"
        >
          <LogOut size={20} />
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50
            bg-white border-r border-gray-200
            transform transition-all duration-300 ease-in-out
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:translate-x-0
            ${sidebarExpanded ? 'lg:w-64' : 'lg:w-20'}
            w-64 h-screen lg:h-screen
          `}
          onMouseEnter={() => {
            if (sidebarCollapsed) setHoverPeek(true);
          }}
          onMouseLeave={() => setHoverPeek(false)}
        >
          <div className="h-full flex flex-col">
            {/* Brand / Collapse */}
            <div className="p-5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-lg">R</span>
                  </div>
                  {sidebarExpanded && (
                    <div className="min-w-0">
                      <h1 className="text-lg font-bold text-gray-900 truncate">RoadServe</h1>
                      <p className="text-xs text-gray-500">Admin Portal</p>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSidebarCollapsed((v) => !v);
                    setHoverPeek(false);
                  }}
                  className="hidden lg:inline-flex p-2 rounded-lg hover:bg-gray-100 shrink-0"
                  title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {sidebarCollapsed && !hoverPeek ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                </button>
              </div>

              {sidebarExpanded && (
                <div className="relative mt-3">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    value={menuSearch}
                    onChange={(e) => setMenuSearch(e.target.value)}
                    placeholder="Search menu..."
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 outline-none focus:bg-white focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
                  />
                </div>
              )}
            </div>

            {/* Nav (scrollable) */}
            <nav className="flex-1 overflow-y-auto p-3 space-y-1">
              {filteredMenuItems.length === 0 ? (
                <p className={`text-xs text-gray-400 py-4 ${sidebarExpanded ? 'px-2' : 'text-center'}`}>
                  {sidebarExpanded ? 'No matches' : '—'}
                </p>
              ) : (
                filteredMenuItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      title={!sidebarExpanded ? item.label : undefined}
                      aria-current={active ? 'page' : undefined}
                      className={`
                        group relative flex items-center
                        ${sidebarExpanded ? 'space-x-3 px-4' : 'justify-center px-3'}
                        py-2.5 rounded-lg transition
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2
                        ${active
                          ? 'bg-primary-50 text-primary-700 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'
                        }
                      `}
                    >
                      <span
                        className={`
                          absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r
                          ${active ? 'bg-primary-600' : 'bg-transparent group-hover:bg-gray-200'}
                        `}
                      />
                      <span
                        className={`
                          inline-flex items-center justify-center
                          w-9 h-9 rounded-lg
                          ${active ? 'bg-white' : 'bg-transparent group-hover:bg-white'}
                          transition
                        `}
                      >
                        <Icon size={20} />
                      </span>
                      {sidebarExpanded && (
                        <span className="text-sm truncate">
                          {item.label}
                        </span>
                      )}
                    </Link>
                  );
                })
              )}
            </nav>

            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-white">
              <div className={`flex items-center ${sidebarExpanded ? 'space-x-3 px-2' : 'justify-center'} mb-3`}>
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                  <span className="text-primary-700 font-semibold text-sm">
                    {user?.email?.charAt(0).toUpperCase() || 'A'}
                  </span>
                </div>
                {sidebarExpanded && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user?.email || 'Admin'}
                    </p>
                    <p className="text-xs text-gray-500">Administrator</p>
                  </div>
                )}
              </div>
              <button
                onClick={onLogout}
                className={`
                  w-full flex items-center
                  ${sidebarExpanded ? 'space-x-3 px-2' : 'justify-center'}
                  py-2.5 rounded-lg text-red-600 hover:bg-red-50 transition
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2
                `}
                title="Logout"
              >
                <LogOut size={20} />
                {sidebarExpanded && <span className="text-sm font-medium">Logout</span>}
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-0 min-w-0">
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
